import { ComponentPortal } from '@angular/cdk/portal';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Injector, Input, Output } from '@angular/core';
import { WorkflowDebugHelper } from '../../helpers/workflow.debug.helper';
import { TranslatedString } from '../../models/translatedstring.model';
import {
    DebugSessionMessage, DebugSessionState, DebugSessionStateDataType, NextStep, NextStepInfo
} from '../../models/workflow/debug.log.message';
import { BreakPointInfo } from '../../models/workflow/workflow.debug.settings';
import { WorkflowSaveObject } from '../../models/workflow/workflow.model';
import { DebugService } from '../../services/debug.service';
import { WF_REGISTRY } from '../../services/workflow.service';

@Component({
    selector: 'debug-workflow-state',
    templateUrl: './debug.workflow.state.control.html',
    styleUrls: ['./debug.workflow.state.control.css']
})
export class DebugWorkflowStateControl implements AfterViewInit {
    Initialized = false;
    editorOptions = {
        automaticLayout: true,
        language: 'json',
        readOnly: true,
        scrollBeyondLastLine: false
    };

    Waiting = false;
    Script = '';

    /**
     * Die Variablen des angehaltenen Schrittes, als Namen.
     *
     * Der Status traegt seit dem Umbau keine Werte mehr, sondern
     * Beschreibungen -- Name, Typ, Anzahl, kurze Vorschau. Ein Workflow mit
     * einer gelesenen Tabelle hat vorher bei jedem Klick alles uebertragen.
     */
    Variables: { Name: string, Type: string, Count?: number, Preview?: string }[] = [];
    SelectedVariable: string = null;
    LoadingVariable = false;

    /**
     * Ob der Status die Werte selbst traegt statt nur Beschreibungen.
     *
     * Aus `ValuesOmitted` des Status. Eine Gegenstelle vor dem Umbau schickt
     * weiterhin alles mit und kennt `api/debug/GetVariable` nicht -- dort waere
     * jede Anfrage vergeblich, obwohl der Wert bereits vorliegt.
     */
    ValuesInline = false;
    StateSettings = {
        Breakpoints: []
    };
    StateData: {
        Key: string,
        DataType: DebugSessionStateDataType,
        Data
    };
    private LoadedWorkflows = {};

    //#region Data
    DataValue: DebugSessionState;

    @Input()
    get Data() {
        return this.DataValue;
    }
    set Data(val) {
        if (val !== this.DataValue) {
            this.DataValue = val;
            this.UpdateContent();
        }
    }
    //#endregion

    @Output() Cancel = new EventEmitter<any>();
    CaptionValue: string;
    @Output() CaptionChange = new EventEmitter<string>();

    constructor(private cdRef: ChangeDetectorRef, private service: DebugService, private _injector: Injector) {
    }

    ngAfterViewInit() {
        this.Initialized = true;
        this.UpdateContent();
    }

    changeCaption(caption: string) {
        if (caption !== this.CaptionValue) {
            this.CaptionValue = caption;
            this.CaptionChange.emit(this.CaptionValue);
        }
    }

    play(clientID, runID) {
        this.sendStepMessage(NextStep.Play);
    }

    stepInto(clientID, runID) {
        this.sendStepMessage(NextStep.Into);
    }

    stepOver(clientID, runID) {
        this.sendStepMessage(NextStep.Next);
    }

    stepOut(clientID, runID) {
        this.sendStepMessage(NextStep.Out);
    }

    cancel(clientID, runID) {
        this.sendStepMessage(NextStep.Cancel);
        this.Cancel.emit();
    }

    private sendStepMessage(nextStep: NextStep) {
        this.Waiting = true;
        const message = new DebugSessionMessage();
        message.Target = this.DataValue.ClientID;
        message.Type = 'NextStep';
        const content = new NextStepInfo();
        content.RunID = this.DataValue.RunID;
        content.StepIntoRunID = this.DataValue.StepIntoRunID;
        content.NextStep = nextStep;
        content.Breakpoints = this.StateSettings.Breakpoints;
        message.Content = content;
        WorkflowDebugHelper.SendDebugMessage(message);
    }

    private UpdateContent() {
        if (this.Initialized) {
            this.Waiting = false;
            if (this.DataValue) {
                this.Script = JSON.stringify(this.DataValue.Status, null, 4);
                this.FillVariables();
                if (this.DataValue.Data) {
                    const stateData = this.DataValue.Data.Data;
                    if (stateData) {
                        this.StateSettings = {
                            Breakpoints: stateData.Breakpoints ?? []
                        };
                        switch (this.DataValue.Data.DataType) {
                            case DebugSessionStateDataType.Workflow:
                                const wfKey = stateData.PageSID + '_' + stateData.WorkflowID;
                                if (this.StateData && this.StateData.DataType === this.DataValue.Data.DataType && this.StateData.Key === wfKey) {
                                    this.StateData.Data.Selection = {
                                        ModuleID: stateData.ModuleID,
                                        Start: stateData.Start
                                    };
                                    this.cdRef.detectChanges();
                                } else {
                                    this.GetWorkflow(stateData).then(wf => {
                                        this.StateData = {
                                            Key: wfKey,
                                            DataType: this.DataValue.Data.DataType,
                                            Data: {
                                                Workflow: wf,
                                                Selection: {
                                                    ModuleID: stateData.ModuleID,
                                                    Start: stateData.Start
                                                }
                                            }
                                        }
                                        this.changeCaption(wf.Caption + ' (' + this.DataValue.RunID + ')');
                                        this.cdRef.detectChanges();
                                    });
                                }
                                break;
                            case DebugSessionStateDataType.Module:
                                const modKey = stateData.PageSID + '_' + stateData.WorkflowID + '_' + stateData.ModuleID;
                                if (this.StateData && this.StateData.DataType === this.DataValue.Data.DataType &&
                                    this.StateData.Key === modKey && this.StateData.Data && this.StateData.Data.Handler) {
                                    let abp;
                                    const bpi = this.StateSettings.Breakpoints.find(bp => bp.ModuleID === stateData.ModuleID);
                                    if (bpi) {
                                        abp = bpi.AdditionalBreakPoints;
                                    }
                                    this.StateData.Data.Handler.updateControlData(this.StateData.Data.Data, abp, stateData);
                                    this.cdRef.detectChanges();
                                } else {
                                    this.GetWorkflow(stateData).then(wf => {
                                        let data;
                                        if (wf && wf.Data) {
                                            const mod = WorkflowSaveObject.DeserializeModuleSettings(wf.Data.Modules.find(x => x.ID === stateData.ModuleID));
                                            if (mod && mod.Settings) {
                                                const desc = WF_REGISTRY.get(mod.Module);
                                                if (desc && desc.SettingsTypeHelper) {
                                                    const abh = desc.SettingsTypeHelper.getAdditionalBreakpointHandler(mod.Settings);
                                                    if (abh) {
                                                        let breakpoints = stateData.Breakpoints ?? [];
                                                        let bpi = breakpoints.find(bp => bp.ModuleID === stateData.ModuleID);
                                                        if (!bpi) {
                                                            bpi = new BreakPointInfo();
                                                            bpi.ModuleID = stateData.ModuleID;
                                                            bpi.AdditionalBreakPoints = abh.EmptyData;
                                                            breakpoints.push(bpi);
                                                        } else if (!bpi.AdditionalBreakPoints) {
                                                            bpi.AdditionalBreakPoints = abh.EmptyData;
                                                        }
                                                        const controlData = {};
                                                        abh.updateControlData(controlData, bpi.AdditionalBreakPoints, stateData);
                                                        const injectorTokens = new WeakMap();
                                                        injectorTokens.set(abh.Token, controlData);
                                                        data = {
                                                            Key: modKey,
                                                            DataType: this.DataValue.Data.DataType,
                                                            Data: {
                                                                Handler: abh,
                                                                Data: controlData,
                                                                ModuleTemplate: new ComponentPortal(abh.ComponentType, null, Injector.create({
                                                                    providers: [{
                                                                        provide: abh.Token,
                                                                        useValue: controlData
                                                                    }],
                                                                    parent: this._injector
                                                                })),
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                        this.StateData = data;
                                        this.changeCaption(wf.Caption + ' (' + this.DataValue.RunID + ')');
                                        this.cdRef.detectChanges();
                                    });
                                }
                                break;
                        }
                    } else {
                        this.StateData = null;
                        this.StateSettings = {
                            Breakpoints: []
                        };
                        this.changeCaption(this.DataValue.RunID);
                        this.cdRef.detectChanges();
                    }
                } else {
                    this.StateData = null;
                    this.StateSettings = {
                        Breakpoints: []
                    };
                    this.changeCaption(this.DataValue.RunID);
                    this.cdRef.detectChanges();
                }
            } else {
                this.Script = '';
                this.StateData = null;
                this.StateSettings = {
                    Breakpoints: []
                };
                this.changeCaption(this.DataValue.RunID);
                this.cdRef.detectChanges();
            }
        }
    }

    /** Die Namensliste aus dem Status; bei jedem Schritt neu. */
    private FillVariables() {
        this.Variables = [];
        this.SelectedVariable = null;
        const status: any = this.DataValue?.Status;
        if (!status) {
            return;
        }
        // Traegt der Status die Werte selbst, oder nur Beschreibungen? Ein
        // Status ohne das Flag kommt von einer Gegenstelle vor dem Umbau und
        // enthaelt die vollen Werte -- die sind dann schon da und werden nicht
        // noch einmal geholt.
        this.ValuesInline = status.ValuesOmitted !== true;
        const add = (name: string, eintrag: any) => {
            if (eintrag === null || typeof eintrag === 'undefined') {
                return;
            }
            const beschreibung = this.ValuesInline ? DebugWorkflowStateControl.Describe(eintrag) : eintrag;
            this.Variables.push({
                Name: name,
                Type: beschreibung.Type ?? typeof eintrag,
                Count: beschreibung.Count,
                Preview: beschreibung.Preview
            });
        };
        if (status.ActualParameter) {
            add('ActualParameter', status.ActualParameter);
        }
        if (status.Context) {
            Object.keys(status.Context).forEach(key => add(key, status.Context[key]));
        }
        this.Variables.sort((a, b) => a.Name.localeCompare(b.Name));
    }

    /**
     * Beschreibt einen mitgelieferten Wert so, wie die Gegenstelle ihn
     * beschrieben haette -- damit die Liste in beiden Faellen gleich aussieht.
     */
    private static Describe(value: any): { Type: string, Count?: number, Preview?: string } {
        if (Array.isArray(value)) {
            return { Type: 'Array', Count: value.length };
        }
        if (typeof value === 'string') {
            return {
                Type: 'String',
                Count: value.length,
                Preview: value.length > 120 ? value.substring(0, 120) + '…' : value
            };
        }
        if (typeof value === 'object') {
            return { Type: 'Object' };
        }
        return { Type: typeof value, Preview: String(value) };
    }

    /**
     * Holt den Wert einer Variablen -- erst jetzt, nicht bei jedem Schritt.
     *
     * Zwei Wege, weil der Wert an zwei verschiedenen Orten liegt: bei einem
     * **Service**-Workflow im Dienst, per REST erreichbar; bei einem
     * **Client**-Workflow in einem anderen Browser, an den nur der Debug-Kanal
     * herankommt.
     */
    showVariable(name: string) {
        if (!this.DataValue) {
            return;
        }
        this.SelectedVariable = name;
        this.LoadingVariable = true;
        this.cdRef.detectChanges();

        const fertig = (value: any, gefunden: boolean) => {
            this.LoadingVariable = false;
            this.Script = gefunden
                ? JSON.stringify(value, null, 4)
                : '// ' + name + ': nicht ermittelbar';
            this.cdRef.detectChanges();
        };

        // Liegt der Wert schon im Status, wird nichts geholt. Sonst wuerde die
        // Konsole eine Gegenstelle nach etwas fragen, das sie gerade selbst
        // mitgeschickt hat -- bei einem alten Dienst gibt es den Aufruf gar
        // nicht, und der Wert waere trotz Anwesenheit "nicht ermittelbar".
        if (this.ValuesInline) {
            const status: any = this.DataValue.Status;
            if (name === 'ActualParameter') {
                fertig(status?.ActualParameter, typeof status?.ActualParameter !== 'undefined');
            } else {
                const vorhanden = !!status?.Context && Object.prototype.hasOwnProperty.call(status.Context, name);
                fertig(vorhanden ? status.Context[name] : null, vorhanden);
            }
            return;
        }

        const clientID = this.DataValue.ClientID ?? '';
        if (clientID.endsWith('_service')) {
            this.service.GetVariable(this.DataValue.StepIntoRunID, name).subscribe(x => {
                fertig(x?.Value, x?.Found === true);
            }, () => fertig(null, false));
        } else {
            WorkflowDebugHelper.RequestVariable(clientID, this.DataValue.StepIntoRunID, name)
                .then(value => fertig(value, typeof value !== 'undefined'));
        }
    }

    /** Zurueck auf die Uebersicht, ohne einen Wert zu holen. */
    showOverview() {
        this.SelectedVariable = null;
        this.Script = JSON.stringify(this.DataValue?.Status, null, 4);
        this.cdRef.detectChanges();
    }

    private GetWorkflow(data) {
        return new Promise<any>(resolve => {
            const key = data.PageSID + '_' + data.WorkflowID;
            const wf = this.LoadedWorkflows[key];
            if (wf) {
                resolve(wf);
            } else {
                if (data.AdditionalData) {
                    let wfData = JSON.parse(data.AdditionalData);
                    if (data.PageSID === 'service') {
                        wfData = {
                            Data: wfData,
                            Caption: TranslatedString.GetTranslation(wfData.Caption)
                        };
                    }
                    this.LoadedWorkflows[key] = wfData;
                    resolve(wfData);
                } else {
                    this.service.GetWorkflow(data.WorkflowID, data.PageSID).subscribe(x => {
                        if (x && (data.PageSID === 'service' || data.PageSID === 'template')) {
                            x = {
                                Data: x,
                                Caption: TranslatedString.GetTranslation(x.Caption)
                            }
                        }
                        this.LoadedWorkflows[key] = x;
                        resolve(x);
                    });
                }
            }
        });
    }
}
